<template>
  <div class="customer__form modal modal--default">
    <div class="modal__panel">
      <div class="modal__logo-wrapper">
        <div class="modal__logo"></div>
      </div>

      <div class="modal__main-icon modal__main-icon--mandat"></div>
      <div class="modal__back" @click="goBack()"></div>
      <div class="modal__close" @click="closeModal()"></div>

      <div class="form form--terminate">
        <div class="navigators">
          <div class="navigator navigator--active">
            <div class="navigator__bullet"></div>
            <div class="navigator__title">
              {{ $t(`addMandateModal.${"title"}`) }}
            </div>
          </div>
        </div>
        <div class="add__form">
          <transition name="fade" mode="out-in">
            <Loading v-if="formLoading" />
            <div v-else class="add__content">
              <div class="termination">
                <div class="termination__row">
                  <div class="termination__column">
                    <div class="form__field">
                      <div class="input input--default input--readonly">
                        {{ parseCustomerName(customerDetails) }}
                      </div>
                    </div>

                    <div class="form__field">
                      <VSelect class="select select--default" :options="agents" :reduce="(agent) => agent.id"
                        :getOptionLabel="(agent) => `${agent.first_name} ${agent.last_name}`" v-model="agentId"
                        :placeholder="$t(`addMandateModal.${'agent'}`)" />
                    </div>

                    <div class="form__field">
                      <VSelect class="select select--default" :class="{ 'select--error': mandateHasError }"
                        :options="mandates" :reduce="(mandate) => mandate.id"
                        :getOptionLabel="(mandate) => `${mandate.name} `"
                        :placeholder="$t(`addMandateModal.${'mandate'}`)" v-model="mandateTypeId" />
                      <div class="form__error" v-if="mandateHasError">
                        {{ $t(`addMandateModal.${"mandateErrorMsg"}`) }}
                      </div>
                    </div>

                    <div class="form__field">
                      <VSelect class="select select--default" :options="agents" :reduce="(agent) => agent.id"
                        :getOptionLabel="(agent) => `${agent.first_name} ${agent.last_name}`" v-model="noticeAgentId"
                        :placeholder="$t(`addMandateModal.${'noticeAgent'}`)" />
                    </div>

                    <div class="form__field team-select-input">
                      <MultiSelectDropdown :items="companies" placeholder="Select companies" label="name"
                        :selected="emailData.companies" class="team-select" :class="{ 'input--error': companyHasError }"
                        @input="onCompaniesSelected($event)" />
                      <div class="form__error" v-if="companyHasError">
                        Please select a company
                      </div>
                    </div>

                    <div class="form__field  company-policies">
                      <div v-for="company in emailData.companies" :key="company.id" class="company-policy-row">
                        <div class="company-name-wrapper">
                          <div class="company-name">

                            <button @click="removeCompany(company.id)" type="button" class="button remove-company-btn"
                              title="Remove this company">
                              <div class="button__icon"></div>
                            </button>
                            <p>{{ company.name }}</p>
                          </div>
                        </div>

                        <div class="policy-numbers">
                          <div class="policy-numbers-container">
                            <div v-for="(policyNum, index) in company.policyNumbers" :key="index"
                              class="policy-number-row">
                              <div class="form__field">
                                <input v-model="company.policyNumbers[index]" type="text" class="input input--default"
                                  placeholder="Policy Nr." @keydown.enter.prevent />
                              </div>

                              <button v-if="company.policyNumbers.length > 1"
                                @click="removePolicyNumber(company.id, index)" type="button"
                                class="button delete-policy-btn policy-btn">
                                <div class="button__icon"></div>
                              </button>
                            </div>
                          </div>
                          <button @click="addPolicyNumber(company.id)"
                            class="button button--default policy-btn add-policy-btn">
                            <div class="button__icon"></div>
                          </button>

                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>

            </div>
          </transition>
        </div>

      </div>
      <div v-if="isRequesting" class="modal__button modal__button--submit button button--default button--loading"></div>
      <div v-else>
        <button class="modal__button modal__button--submit button button--default" @click="addMandate()">
          <span>{{ $t(`addMandateModal.${'saveBtn'}`) }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from "vue";
import { useStore } from "vuex";
import Loading from "@/partials/Loading";
import { parseCustomerName } from "@/helpers/entityParsers";
import MultiSelectDropdown from "@/partials/MultiSelectDropdown";

defineOptions({
  name: "AddMandateModal"
});

const store = useStore();

// Reactive properties
const formLoading = ref(true);
const isRequesting = ref(false);

const customerId = ref("");
const mandateTypeId = ref("");

const agentId = ref(null);
const noticeAgentId = ref(null);

const mandateHasError = ref(false);
const companyHasError = ref(false);

const mandateCompaniesForms = computed(() => store.getters.mandateCompaniesForms);

const companies = computed(() => {
  return mandateCompaniesForms.value?.companies || [];
});

const emailData = ref({
  companies: [],
});
// Computed properties (Mapping Vuex getters and store mappings)
const customerDetails = computed(() => store.getters.customerDetails);
const mandateForms = computed(() => store.getters.mandateForms);

const agents = computed(() => mandateForms.value.agents);
const mandates = computed(() => mandateForms.value.mandate_types);

// Watchers
watch(mandateTypeId, (value) => {
  mandateHasError.value = !value;
});

// Operational Methods
const closeModal = () => {
  store.commit("setShowAddMandateModal", false);
};

const goBack = () => {
  closeModal();
  store.commit("setShowAddActivityModal", true);
};

const validateFields = (callback) => {
  mandateHasError.value = !mandateTypeId.value;
  companyHasError.value = emailData.value.companies.length === 0;

  callback(
    !mandateHasError.value &&
    !companyHasError.value
  );
};

const addMandate = async () => {
  isRequesting.value = true;

  validateFields(async (isValid) => {
    if (!isValid) {
      store.commit("setToast", {
        display: true,
        type: "error",
        message: store.getters.customerDetails ? store.getters.customerDetails && store.getters.customerDetails.id ? store.commit("setToast", { display: true, type: "error", message: store.getters.customerDetails ? store.getters.customerDetails && store.getters.customerDetails.id ? "" : "" : "" }) : "" : ""
      });

      // Fallback matching your exact localization error string logic safely
      store.commit("setToast", {
        display: true,
        type: "error",
        // Direct context injection since $t cannot evaluate directly during root setup boundaries safely without returning explicit text string keys
        message: window.Vue?.$ioc?._i18n?.t(`addMandateModal.validationErrorMsg`),
      });
      isRequesting.value = false;
      return;
    }

    try {
      const deliveries = emailData.value.companies.map(company => ({
        company_id: company.id,
        policy_numbers: company.policyNumbers.filter(num => num.trim()),
      }));

      await store.dispatch("createMandate", {
        customer_id: customerId.value,
        mandate_type_id: mandateTypeId.value,
        agent_id: agentId.value,
        notice_agent_id: noticeAgentId.value,
        deliveries,
      });

      closeModal();
    } catch (error) {
      console.error("Action failed:", error);
    } finally {
      isRequesting.value = false;
    }
  });
};
const onCompaniesSelected = (selectedCompanies) => {
  const existingIds = new Set(emailData.value.companies.map(c => c.id));
  const selectedIds = new Set(selectedCompanies.map(c => c.id));

  emailData.value.companies =
    emailData.value.companies.filter(c => selectedIds.has(c.id));

  selectedCompanies.forEach(company => {
    if (!existingIds.has(company.id)) {
      emailData.value.companies.push({
        id: company.id,
        name: company.name,
        policyNumbers: [""],
      });
    }
  });
};

const addPolicyNumber = (companyId) => {
  const company = emailData.value.companies.find(c => c.id === companyId);
  if (company) company.policyNumbers.push("");
};

const removePolicyNumber = (companyId, index) => {
  const company = emailData.value.companies.find(c => c.id === companyId);
  if (company) company.policyNumbers.splice(index, 1);
};

const removeCompany = (companyId) => {
  emailData.value.companies =
    emailData.value.companies.filter(c => c.id !== companyId);
};
// Initialization Block (Replaces created Lifecycle hook logic)
const initializeComponent = async () => {
  formLoading.value = true;

  if (customerDetails.value?.team) {
    const teamId = Number(customerDetails.value.team.id);

    await Promise.all([
      store.dispatch("getMandateForms", teamId),
      store.dispatch("getMandateCompaniesForms", teamId),
    ]);
  }

  customerId.value = customerDetails.value?.id || "";
  formLoading.value = false;
};

initializeComponent();

// Cleanup hooks logic
onUnmounted(() => {
  closeModal();
});
</script>

<style scoped lang="scss">
@use "../assets/scss/termination-potential-policy";

.termination__column {
  &:last-child {
    flex: 1;
  }
}

.modal__panel {
  max-width: 600px !important;
}

.add__form {
  width: 100% !important;
}

.custom-duration-wrapper {

  background-color: var(--bg-3);

  color: var(--text-3);
  padding: 0 10px;
  font-size: var(--x-small);
  border-radius: var(--radius-1);
  font-family: var(--muli-text-semi-bold);
  border: 1px solid transparent;


  display: flex;
  align-items: center;
  padding: 0;

}

.custom-duration {
  background-color: transparent;
  //  border: 1px solid ;
  height: 40px;
  color: var(--text-3);

  font-size: var(--x-small);
  text-align: center;
  font-family: var(--muli-text-semi-bold);
}


.time-picker {
  width: 100%;
  padding: .3em .5em;
  font-size: 1em;
  height: 100%;
  background-color: transparent;
  border: none;
  text-align: center;
  color: var(--text-2);
  font-family: var(--muli-text-semi-bold);
}

.termination__row {
  border: 0;
}

.form__field__call__duration {
  margin-top: -8px;
}

.call__duration__label {
  margin-bottom: -6px;
}

[data-theme="dark"] .call__duration__label {
  p {
    color: white;
  }
}


.call__duration__label p {
  color: #7F8FA480;
  font-size: 14px;
}


.call__duration__types {
  display: flex;
  gap: 12px;
}


.call__duration__value {
  color: #B9C2CD;
  font-size: 16px;
  cursor: pointer;
}


.call__duration__value--active {
  color: #22DB8E;
}

.termination__textarea__row {
  margin-top: -40px;
}

.form__notes__textarea {
  width: 66%;
}


.custom-time-input {
  position: relative;
  width: 48%;
}
.customer__form .form--terminate{
  width: 100%;
}
.customer__form .form .add__content {
    padding: 0 25px;
    height: 84%;
    overflow: auto;
}
.custom-input {
  appearance: none;
  color: var(--text-2);
  background-color: var(--bg-3);
  border-radius: 10px;
  padding: 0 10px;
  height: 40px;
  font-size: 16px;
  width: 100%;
  box-sizing: border-box;
}

.custom-input::placeholder {
  font-family: var(--muli-text-semi-bold);
  color: var(--text-8);
}


.date-picker {
  width: 48%;
  position: relative;
  z-index: 1;
}

.date-time-wrapper {
  display: flex;
  justify-content: space-between;
}

.modal__panel {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.form {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.add__form {
  flex: 1;
  min-height: 0;
}

.add__content {
  min-height: 0;
  height: 84%;
}

 
.team-select-input {
  width: 100%;
  margin: 0 auto;
  margin-bottom: 40px;
  flex-shrink: 0;
}

.company-policies {
  display: flex;
  flex-direction: column;
  gap: 20px;
  // overflow-y: auto;
  flex: 1;
}

.company-policy-row {
  display: flex;
  align-items: flex-start;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--bg-2);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0px;
  }

  .company-name-wrapper {
    width: 50%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 8px;
  }

  .company-name {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;

    p {
      color: var(--text-2);
      font-size: 16px;
    }

    button {
      transform: scale(0.8);
    }
  }

  .policy-numbers {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    width: 50%;
  }

  .policy-numbers-container {
    width: 90%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;

    .policy-number-row {
      display: flex;
      gap: 5px;
      width: 100%;

      .form__field {
        width: 90%;
        margin-bottom: 0;

        input {
          width: 100%;
        }
      }
    }
  }

  .policy-btn {
    transform: scale(0.7);
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    .button__icon {
      margin: auto;
      width: 20px;
      height: 20px;
      background-image: url("../assets/icons/default/add_icon.svg");
      background-repeat: no-repeat;
      background-position: center;
    }
  }

  .delete-policy-btn {
    background-color: #f44336;

    &:hover {
      background-color: #da190b;
      box-shadow: 0px -7px 18px rgba(0, 0, 0, 0.2) inset;
    }

    .button__icon {
      transform: rotate(45deg) !important;
    }
  }

  .remove-company-btn {
    background-color: #f44336;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    &:hover {
      background-color: #da190b;
      box-shadow: 0px -7px 18px rgba(0, 0, 0, 0.2) inset;
    }

    .button__icon {
      width: 16px;
      height: 16px;
      background-image: url("../assets/icons/default/add_icon.svg");
      background-repeat: no-repeat;
      background-position: center;
      transform: rotate(45deg);
    }
  }
  
}
</style>
